import React, { useState, useEffect } from 'react';
import './App.css';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import VerifyConsent from './pages/VerifyConsent';
import ConsentResult from './pages/ConsentResult';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import SubjectPage from './pages/SubjectPage';
import BlogIndex from './pages/BlogIndex';
import BlogPostPage from './pages/BlogPost';
import SiteNav from './components/SiteNav';
import SiteFooter from './components/SiteFooter';
import { SUBJECTS } from './data/subjects';
import { BLOG_POSTS } from './data/blogPosts';
import { Analytics } from '@vercel/analytics/react';

const SUPABASE_URL = 'https://kvscdxgfofbcewkysbvi.supabase.co';
const SUPABASE_ANON_KEY = 'sb_publishable_zrqqcBLkq5l9Sqdi-pM9wg_732O3Sxf';

const BASE_URL = 'https://elevenup.xyz';

interface SEOProps {
  title: string;
  description: string;
  path: string;
  schema?: object;
}

function useSEO({ title, description, path, schema }: SEOProps) {
  useEffect(() => {
    document.title = title;

    const url = `${BASE_URL}${path}`;

    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (el) {
        el.setAttribute('content', content);
      } else {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        el.setAttribute('content', content);
        document.head.appendChild(el);
      }
    };

    setMeta('name', 'description', description);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', url);
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonical) {
      canonical.href = url;
    } else {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = url;
      document.head.appendChild(canonical);
    }

    // Dynamic schema markup — remove old, inject new
    const oldSchema = document.getElementById('dynamic-schema');
    if (oldSchema) oldSchema.remove();

    if (schema) {
      const script = document.createElement('script');
      script.id = 'dynamic-schema';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }
  }, [title, description, path, schema]);
}

// --- Schema helpers ---
const ORG_SCHEMA = {
  '@type': 'Organization',
  name: 'ElevenUp',
  url: 'https://elevenup.xyz',
  logo: 'https://elevenup.xyz/ElevenUpLogo.png',
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'rain@elevenup.xyz',
    telephone: '+447957733057',
    contactType: 'customer service',
  },
};

const WEBSITE_SCHEMA = {
  '@type': 'WebSite',
  name: 'ElevenUp',
  url: 'https://elevenup.xyz',
};

function makeHomepageSchema(faqs: { question: string; answer: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      ORG_SCHEMA,
      WEBSITE_SCHEMA,
      {
        '@type': 'WebApplication',
        name: 'ElevenUp',
        description: 'AI-powered 11+ exam preparation with 10,000+ practice questions across Maths, English, Verbal Reasoning, and Non-Verbal Reasoning.',
        url: 'https://elevenup.xyz',
        applicationCategory: 'EducationalApplication',
        operatingSystem: 'All',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'GBP',
          description: 'Free tier with optional premium upgrade',
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map((f) => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: { '@type': 'Answer', text: f.answer },
        })),
      },
    ],
  };
}

function makeSubjectSchema(subject: { name: string; slug: string; description: string; totalQuestions: number; topics: { name: string; description: string }[] }) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      ORG_SCHEMA,
      makeBreadcrumbs([{ name: subject.name, path: `/${subject.slug}` }]),
      {
        '@type': 'Course',
        name: `11+ ${subject.name} Practice`,
        description: subject.description,
        url: `${BASE_URL}/${subject.slug}`,
        provider: { '@type': 'Organization', name: 'ElevenUp' },
        educationalLevel: 'Primary School (Year 4-6)',
        numberOfCredits: subject.totalQuestions,
        hasCourseInstance: subject.topics.map((t) => ({
          '@type': 'CourseInstance',
          name: t.name,
          description: t.description,
        })),
      },
    ],
  };
}

function makeBlogPostSchema(post: { title: string; slug: string; date: string; author: string; excerpt: string }) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      ORG_SCHEMA,
      makeBreadcrumbs([{ name: 'Blog', path: '/blog' }, { name: post.title, path: `/blog/${post.slug}` }]),
      {
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.excerpt,
        url: `${BASE_URL}/blog/${post.slug}`,
        datePublished: post.date,
        dateModified: post.date,
        author: { '@type': 'Person', name: post.author },
        publisher: { '@type': 'Organization', name: 'ElevenUp', logo: { '@type': 'ImageObject', url: 'https://elevenup.xyz/ElevenUpLogo.png' } },
      },
    ],
  };
}

function makeBlogIndexSchema() {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      ORG_SCHEMA,
      makeBreadcrumbs([{ name: 'Blog', path: '/blog' }]),
      {
        '@type': 'Blog',
        name: 'ElevenUp Blog',
        description: 'Expert advice on 11+ exam preparation.',
        url: `${BASE_URL}/blog`,
      },
    ],
  };
}

function makeBreadcrumbs(items: { name: string; path: string }[]) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
      ...items.map((item, i) => ({
        '@type': 'ListItem',
        position: i + 2,
        name: item.name,
        item: `${BASE_URL}${item.path}`,
      })),
    ],
  };
}

const PAGE_SEO: Record<string, SEOProps> = {
  '/': {
    title: 'ElevenUp: AI 11+ Exam Prep | 10,000+ Questions',
    description: 'AI-powered 11+ exam prep with 10,000+ practice questions. Maths, English, Verbal & Non-Verbal Reasoning. Free trial — costs 1/10th of tutoring.',
    path: '/',
  },
  '/privacy': {
    title: 'Privacy Policy | ElevenUp | Student Data Protection',
    description: "ElevenUp's privacy policy. We protect student data, comply with UK GDPR, and never share information. No ads, no tracking, full transparency.",
    path: '/privacy',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        ORG_SCHEMA,
        makeBreadcrumbs([{ name: 'Privacy Policy', path: '/privacy' }]),
        {
          '@type': 'WebPage',
          name: 'Privacy Policy',
          url: `${BASE_URL}/privacy`,
          description: 'ElevenUp privacy policy — how we protect student data and comply with UK GDPR.',
          isPartOf: { '@type': 'WebSite', name: 'ElevenUp', url: BASE_URL },
        },
      ],
    },
  },
  '/terms': {
    title: 'Terms of Service | ElevenUp | 11+ Exam Prep App',
    description: "Terms for using ElevenUp's AI-powered 11+ exam preparation platform. Read our user agreement, subscription terms, and acceptable use policy.",
    path: '/terms',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        ORG_SCHEMA,
        makeBreadcrumbs([{ name: 'Terms of Service', path: '/terms' }]),
        {
          '@type': 'WebPage',
          name: 'Terms of Service',
          url: `${BASE_URL}/terms`,
          description: 'Terms of service for ElevenUp, the AI-powered 11+ exam preparation app.',
          isPartOf: { '@type': 'WebSite', name: 'ElevenUp', url: BASE_URL },
        },
      ],
    },
  },
  '/verify-consent': {
    title: 'Verify Parental Consent | ElevenUp',
    description: 'Verify parental consent for your child to use ElevenUp, the AI-powered 11+ exam preparation app.',
    path: '/verify-consent',
  },
  '/consent-result': {
    title: 'Consent Confirmed | ElevenUp',
    description: 'Parental consent confirmation for ElevenUp, the AI-powered 11+ exam preparation app.',
    path: '/consent-result',
  },
  '/about': {
    title: 'About ElevenUp | AI 11+ Exam Preparation',
    description: 'Meet the team behind ElevenUp. Built by a teacher and a mum who wanted every child to have a fair shot at the 11+ exam.',
    path: '/about',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        ORG_SCHEMA,
        makeBreadcrumbs([{ name: 'About', path: '/about' }]),
        {
          '@type': 'AboutPage',
          name: 'About ElevenUp',
          url: `${BASE_URL}/about`,
          description: 'Meet the team behind ElevenUp.',
        },
      ],
    },
  },
  '/contact': {
    title: 'Contact ElevenUp | 11+ Support | Get Help Fast',
    description: 'Need help with 11+ exam prep? Contact ElevenUp support. We respond within 24 hours. Email rain@elevenup.xyz for assistance.',
    path: '/contact',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        ORG_SCHEMA,
        makeBreadcrumbs([{ name: 'Contact', path: '/contact' }]),
        {
          '@type': 'ContactPage',
          name: 'Contact ElevenUp',
          url: `${BASE_URL}/contact`,
        },
      ],
    },
  },
  '/maths': {
    title: '11+ Maths Practice | AI Tutor | 5,000+ Questions',
    description: 'Master 11+ Maths with AI tutoring. 5,000+ practice questions across 15 topics. Step-by-step explanations for every answer. Start free today.',
    path: '/maths',
  },
  '/english': {
    title: '11+ English Practice | AI Tutor | Grammar & Vocab',
    description: 'Excel in 11+ English with AI-powered prep. 1,800 questions covering grammar, punctuation, vocabulary, spelling, and figurative language. Try free.',
    path: '/english',
  },
  '/verbal-reasoning': {
    title: '11+ Verbal Reasoning | AI Practice | 17 Topics',
    description: 'Ace 11+ Verbal Reasoning with AI tutoring. 17 question types, 2,900+ questions. Codes, sequences, logic, word puzzles. Free trial.',
    path: '/verbal-reasoning',
  },
  '/non-verbal-reasoning': {
    title: '11+ Non-Verbal Reasoning | AI Prep | Coming Soon',
    description: 'Master 11+ Non-Verbal Reasoning with AI. Pattern recognition, shape sequences, spatial awareness. 1,600+ questions coming soon.',
    path: '/non-verbal-reasoning',
  },
  '/blog': {
    title: 'ElevenUp Blog | 11+ Exam Tips for Parents & Students',
    description: 'Expert 11+ exam advice, study tips, and subject guides. Helping parents navigate grammar school entrance exam preparation successfully.',
    path: '/blog',
    schema: makeBlogIndexSchema(),
  },
};

const AppContent: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faqs = [
    {
      question: "What age is ElevenUp designed for?",
      answer: "ElevenUp is designed for children preparing for the 11+ exam, typically in Year 4, Year 5 or Year 6. The content covers all four key areas: Maths, English, Verbal Reasoning, and Non-Verbal Reasoning."
    },
    {
      question: "How does the AI tutor work?",
      answer: "When your child gets a question wrong, our AI tutor Eli gives a clear, patient explanation \u2014 breaking down the concept step-by-step so they understand why, not just what. It's like having a tireless tutor who never gets frustrated."
    },
    {
      question: "Is it safe for children?",
      answer: "Absolutely. We require parental consent before any child can use the app. The AI tutor only discusses 11+ subjects and politely redirects anything off-topic. We don't collect unnecessary data, don't use advertising, and don't share data with third parties."
    },
    {
      question: "What subjects are covered?",
      answer: "Over 10,000 questions across Maths (15 topics), English (6 topics), Verbal Reasoning (17 topics), and Non-Verbal Reasoning. New content is added regularly."
    },
    {
      question: "How much does it cost?",
      answer: "We offer a generous free tier so your child can try it out. Premium unlocks unlimited daily practice and full access to all features. Pricing details coming at launch."
    },
    {
      question: "Can I track my child's progress?",
      answer: "Yes! You can see exactly what they've practised, which topics they're strong in, and where they need more focus \u2014 all from your phone."
    }
  ];

  useSEO({ ...PAGE_SEO['/'], schema: makeHomepageSchema(faqs) });

  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'Prefer': 'return=minimal',
        },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        const data = await response.json();
        setErrorMessage(data.message || 'Something went wrong. Please try again.');
        setSubmitStatus('error');
      }
    } catch (error) {
      setErrorMessage('Unable to connect. Please try again later.');
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToWaitlist = () => {
    document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="landing-page">
      <SiteNav currentPage="home" onScrollToWaitlist={scrollToWaitlist} />

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">Early Access</div>
            <h1 className="hero-title">
              11+ Exam Prep That<br />
              <span className="hero-title-accent">Actually Makes Sense</span>
            </h1>
            <p className="hero-subtitle">
              10,000+ practice questions with an AI tutor that explains every mistake.
              Your child learns independently, builds real confidence, and you see their progress.
            </p>
            <div className="hero-cta-group">
              <button className="btn-primary btn-large" onClick={scrollToWaitlist}>
                Get Early Access
              </button>
              <p className="hero-cta-note">Free to start. No credit card needed.</p>
            </div>
          </div>
          <div className="hero-visual">
            <div className="phone-mockup">
              <div className="phone-notch"></div>
              <div className="phone-screen">
                <div className="app-preview">
                  {/* App Header */}
                  <div className="preview-header">
                    <div className="preview-back-btn">
                      <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                        <polyline points="15 18 9 12 15 6"/>
                      </svg>
                    </div>
                    <img src="/ElevenUpLogo.png" alt="ElevenUp logo" className="preview-logo" />
                    <div className="preview-progress-ring">
                      <span>7</span>
                    </div>
                  </div>

                  {/* Question Card */}
                  <div className="preview-question-card">
                    <div className="preview-subject-badge maths">Maths</div>
                    <p className="preview-question-text">
                      A train travels at 60 mph. How far will it travel in 2.5 hours?
                    </p>
                  </div>

                  {/* Answer Options */}
                  <div className="preview-options">
                    <div className="preview-option">120 miles</div>
                    <div className="preview-option correct">
                      150 miles
                      <span className="option-emoji">&#10003;</span>
                    </div>
                    <div className="preview-option">180 miles</div>
                    <div className="preview-option">140 miles</div>
                  </div>

                  {/* AI Feedback Panel */}
                  <div className="preview-feedback-panel">
                    <div className="feedback-header">
                      <span className="feedback-emoji">&#127881;</span>
                      <span className="feedback-title">Brilliant!</span>
                    </div>
                    <div className="feedback-explanation">
                      <strong>Distance = Speed x Time</strong>
                      <p>60 mph x 2.5 hours = 150 miles</p>
                    </div>
                    <div className="feedback-continue-btn">
                      Continue
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works — 3 Steps */}
      <section id="how-it-works" className="how-it-works-section">
        <div className="container">
          <h2 className="section-title">How It Works</h2>
          <p className="section-subtitle">Three steps to exam confidence</p>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <div className="step-icon">&#128221;</div>
              <h3>Practice Daily</h3>
              <p>Quick quizzes across Maths, English, and Verbal Reasoning — adapted to your child's level.</p>
            </div>
            <div className="step-connector">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12"/>
                <polyline points="12 5 19 12 12 19"/>
              </svg>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <div className="step-icon">&#129302;</div>
              <h3>Learn From Mistakes</h3>
              <p>AI tutor Eli explains every wrong answer clearly — so they understand why, not just what.</p>
            </div>
            <div className="step-connector">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12"/>
                <polyline points="12 5 19 12 12 19"/>
              </svg>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <div className="step-icon">&#128200;</div>
              <h3>See Real Progress</h3>
              <p>Track streaks, earn badges, and watch your child's confidence grow topic by topic.</p>
            </div>
          </div>
        </div>
      </section>

      {/* What's Inside */}
      <section id="subjects" className="subjects-section">
        <div className="container">
          <h2 className="section-title">Everything They Need</h2>
          <p className="section-subtitle">
            10,000+ questions across all four 11+ subjects
          </p>
          <div className="subjects-grid">
            <div className="subject-card maths">
              <div className="subject-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              </div>
              <h3>Maths</h3>
              <p>Arithmetic, fractions, geometry, problem solving &amp; more</p>
              <span className="subject-count">15 topics</span>
            </div>
            <div className="subject-card english">
              <div className="subject-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                </svg>
              </div>
              <h3>English</h3>
              <p>Grammar, comprehension, vocabulary &amp; spelling</p>
              <span className="subject-count">6 topics</span>
            </div>
            <div className="subject-card vr">
              <div className="subject-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>
              <h3>Verbal Reasoning</h3>
              <p>Codes, sequences, logic, word puzzles</p>
              <span className="subject-count">17 topics</span>
            </div>
            <div className="subject-card nvr">
              <div className="subject-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <rect x="7" y="7" width="3" height="3"/>
                  <rect x="14" y="7" width="3" height="3"/>
                  <rect x="7" y="14" width="3" height="3"/>
                  <rect x="14" y="14" width="3" height="3"/>
                </svg>
              </div>
              <h3>Non-Verbal Reasoning</h3>
              <p>Patterns, shapes, spatial awareness</p>
              <span className="subject-count">Coming soon</span>
            </div>
          </div>
        </div>
      </section>

      {/* Why ElevenUp */}
      <section className="difference-section">
        <div className="container">
          <div className="difference-content">
            <h2 className="section-title light">Why Parents Choose ElevenUp</h2>
            <div className="difference-features">
              <div className="difference-feature">
                <div className="feature-check">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <div>
                  <h4>They Actually Understand Why</h4>
                  <p>When your child gets a question wrong, they don't just see the right answer — they get a patient, step-by-step explanation that helps it click.</p>
                </div>
              </div>
              <div className="difference-feature">
                <div className="feature-check">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <div>
                  <h4>Practice That Doesn't Feel Like Work</h4>
                  <p>Daily streaks, badges, and challenges keep them coming back. No more nagging — they'll remind you it's time to practise.</p>
                </div>
              </div>
              <div className="difference-feature">
                <div className="feature-check">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <div>
                  <h4>Built for Safety</h4>
                  <p>Parental consent required. AI that only discusses 11+ topics. No ads, no tracking, no data sharing. Your child's privacy comes first.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Waitlist Section */}
      <section id="waitlist" className="waitlist-section">
        <div className="container">
          <div className="waitlist-content">
            <h2 className="section-title light">Ready to Give Them a Head Start?</h2>
            <p className="section-subtitle light">
              Join early access — free to start, no credit card needed
            </p>
            <form className="waitlist-form" onSubmit={handleWaitlistSubmit}>
              {submitStatus === 'success' ? (
                <div className="success-message">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  <p>You're on the list! We'll email you when we launch.</p>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <input
                      type="email"
                      placeholder="Your email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="waitlist-input"
                    />
                    <button
                      type="submit"
                      className="waitlist-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Joining...' : 'Join Waitlist'}
                    </button>
                  </div>
                  {submitStatus === 'error' && (
                    <p className="error-message">{errorMessage}</p>
                  )}
                </>
              )}
            </form>
            <p className="waitlist-note">No spam, ever. Just launch updates.</p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq-section">
        <div className="container">
          <h2 className="section-title">Common Questions</h2>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`faq-item ${openFaq === index ? 'open' : ''}`}
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
              >
                <div className="faq-question">
                  <span>{faq.question}</span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points={openFaq === index ? "18 15 12 9 6 15" : "6 9 12 15 18 9"}/>
                  </svg>
                </div>
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

function PageSEO({ seo, children }: { seo: SEOProps; children: React.ReactNode }) {
  useSEO(seo);
  return <>{children}</>;
}

function App() {
  const path = window.location.pathname;

  let page;

  if (path === '/privacy') page = <PageSEO seo={PAGE_SEO['/privacy']}><PrivacyPolicy /></PageSEO>;
  else if (path === '/terms') page = <PageSEO seo={PAGE_SEO['/terms']}><TermsOfService /></PageSEO>;
  else if (path === '/verify-consent') page = <PageSEO seo={PAGE_SEO['/verify-consent']}><VerifyConsent /></PageSEO>;
  else if (path === '/consent-result') page = <PageSEO seo={PAGE_SEO['/consent-result']}><ConsentResult /></PageSEO>;
  else if (path === '/about') page = <PageSEO seo={PAGE_SEO['/about']}><AboutPage /></PageSEO>;
  else if (path === '/contact') page = <PageSEO seo={PAGE_SEO['/contact']}><ContactPage /></PageSEO>;
  else if (path === '/blog') page = <PageSEO seo={PAGE_SEO['/blog']}><BlogIndex /></PageSEO>;
  else if (path.startsWith('/blog/')) {
    const blogSlug = path.replace('/blog/', '');
    const post = BLOG_POSTS.find(p => p.slug === blogSlug);
    if (post) {
      const blogSeo: SEOProps = {
        title: `${post.title} | ElevenUp Blog`,
        description: post.excerpt,
        path: `/blog/${post.slug}`,
        schema: makeBlogPostSchema(post),
      };
      page = <PageSEO seo={blogSeo}><BlogPostPage slug={blogSlug} /></PageSEO>;
    }
  } else {
    // Subject pages
    const subjectSlug = path.replace(/^\//, '');
    if (SUBJECTS[subjectSlug]) {
      const subject = SUBJECTS[subjectSlug];
      const baseSeo = PAGE_SEO[path as keyof typeof PAGE_SEO];
      const seo: SEOProps = baseSeo
        ? { ...baseSeo, schema: makeSubjectSchema(subject) }
        : { title: `11+ ${subject.name} Practice | ElevenUp`, description: subject.description, path, schema: makeSubjectSchema(subject) };
      page = <PageSEO seo={seo}><SubjectPage slug={subjectSlug} /></PageSEO>;
    }
  }

  return (
    <>
      {page || <AppContent />}
      <Analytics />
    </>
  );
}

export default App;
